import global from 'global';
import { get, isFunction } from 'lodash';
import qs from 'qs';
import URL from 'url-parse';
import { UserManager, MetadataService, Log, WebStorageStateStore } from 'oidc-client';
import { AUTHORIZATION } from 'http-header-fields';

import BaseProvider, {
  normalizeAccessObject,
  normalizeIdentity,
  onLogoutSymbol,
  onLoginSymbol,
} from './BaseProvider';
import Fetcher from './Fetcher';

function isPopup() {
  return global.opener && global.opener !== global;
}

function isIframe() {
  try {
    return global.self !== global.top;
  } catch (e) {
    return true;
  }
}

Log.logger = console;

class MetadataServiceDecorator extends MetadataService {
  constructor(settings, JsonServiceCtor, getDefaultMetaData) {
    super(settings, JsonServiceCtor);

    this.getDefaultMetaData = getDefaultMetaData;
  }

  async _getMetadataProperty(name, optional = false) {
    const value = await super._getMetadataProperty(name, optional); // eslint-disable-line

    return value || this.getDefaultMetaData()[name];
  }
}

const defaultSettings = {
  transition: 'popup',
  logging: false,
  automaticSilentRenew: true,
  loadUserInfo: false,
  revokeAccessTokenOnSignout: false,
  defaultMetaData: {},
  storage: global.localStorage,
  postLoginRedirectUrl: null,
  postLogoutRedirectUrl: null,
};

const symbols = {
  settings: Symbol('Settings property'),
  user: Symbol('Current user instance property'),
  userManager: Symbol('User Manager instance property'),
  autoRenewCallback: Symbol('Auto renew callback property'),
  defaultMetaDataContext: Symbol('Default meta data context property'),
};

const toAccessObject = user => normalizeAccessObject({
  authType: 'openid',
  idToken: user.id_token,
  accessToken: user.access_token,
  expiresAt: user.expires_at,
});

function refreshDefaultMetaDataContext() {
  this[symbols.defaultMetaDataContext] = this.getAccessObject();
}

const validateUrl = (authority) => {
  const url = new URL(authority);
  if (url.protocol !== 'https:') {
    throw new Error('Protocol should be https');
  }
};

const createManager = settings => new UserManager({
  ...settings,
  userStore: new WebStorageStateStore({ store: settings.storage }),
});

export default class OidcProvider extends BaseProvider {
  userMonitorTimer = null;

  constructor(settings) {
    super();

    validateUrl(settings.authority);

    const allSettings = {
      silent_redirect_uri: settings.redirect_uri,
      post_logout_redirect_uri: settings.redirect_uri,

      ...defaultSettings,
      ...settings,
    };

    this[symbols.settings] = allSettings;

    Log.level = allSettings.logging ? Log.INFO : Log.NONE;

    this[symbols.defaultMetaDataContext] = {};
    const getDefaultMetaData = isFunction(settings.defaultMetaData)
      ? () => settings.defaultMetaData(this[symbols.defaultMetaDataContext]) || {}
      : () => settings.defaultMetaData || {};

    this[symbols.userManager] = createManager({
      ...allSettings,
      MetadataServiceCtor: client => (
        new MetadataServiceDecorator(client, Fetcher, getDefaultMetaData)
      ),
    });

    this[symbols.userManager].events.addUserLoaded((user) => {
      this[symbols.user] = user;
      this::refreshDefaultMetaDataContext();
      this.startUserMonitoring();
    });

    this[symbols.userManager].events.addUserUnloaded(() => {
      this[symbols.user] = null;
      this.stopUserMonitoring();
    });
  }

  startUserMonitoring() {
    this.stopUserMonitoring();
    this.userMonitorTimer = setInterval(async () => {
      const oldUser = this[symbols.user];
      const newUser = await this.syncUserFromStorage();

      if (oldUser && !newUser) {
        this[onLogoutSymbol]();

        this.stopUserMonitoring();
      }
    }, 1000);
  }

  stopUserMonitoring() {
    if (this.userMonitorTimer) {
      clearInterval(this.userMonitorTimer);
    }
  }

  async syncUserFromStorage() {
    this[symbols.user] = await this[symbols.userManager].getUser();

    if (this[symbols.user]) {
      this.startUserMonitoring();
    } else {
      this.stopUserMonitoring();
    }

    return this[symbols.user];
  }

  async init(options) {
    await super.init(options);

    const user = await this.syncUserFromStorage();

    /**
     * Renew user if expired
     */
    if (user && user.expired) {
      await this.renew();
    }

    if (this.getIdentity()) {
      this.startUserMonitoring();
      await this[onLoginSymbol]();
    }

    this::refreshDefaultMetaDataContext();

    return this;
  }

  async login(options = {}) {
    const { transition, postLoginRedirectUrl } = this[symbols.settings];
    const redirectUrl = options.redirectUrl || postLoginRedirectUrl || global.location.href;
    const data = {
      type: 'signIn',
      transition,
      redirectUrl,
    };

    /**
     * Try to load user from storage before login
     */
    const user = await this.syncUserFromStorage();

    if (user && !user.expired) {
      this::refreshDefaultMetaDataContext();

      return this.getIdentity();
    }

    if (transition === 'redirect') {
      await this[symbols.userManager].signinRedirect({ data });

      /**
       * Return promise to suspend async flow before page redirect
       */
      return new Promise(() => {});
    }

    await this[symbols.userManager].signinPopup({ data });
    await this[onLoginSymbol]();

    return this.getIdentity();
  }

  async logout(options = {}) {
    if (!this[symbols.user]) {
      return true;
    }

    const { transition, postLogoutRedirectUrl } = this[symbols.settings];

    await this.syncUserFromStorage();

    /**
     * Check if user is already logged out
     */
    if (!this.getIdentity()) {
      return true;
    }

    if (transition === 'redirect') {
      await this[symbols.userManager].signoutRedirect({
        data: {
          type: 'signOut',
          transition,
          redirectUrl: options.redirectUrl || postLogoutRedirectUrl || global.location.origin,
        },
      });

      /**
       * Return promise to suspend async flow before page redirect
       */
      return new Promise(() => {});
    }

    try {
      await this[symbols.userManager].signoutPopup({
        data: {
          type: 'signOut',
        },
      });
    } catch (e) {
      /**
       * Check that the user was logged out
       */
      return !await this[symbols.userManager].signinSilent({
        silentRequestTimeout: 3000,
        data: {
          type: 'renew',
        },
      }).catch(() => null);
    }

    if (this[symbols.user]) {
      await this.clear();
    }

    await this[onLogoutSymbol]();

    return true;
  }

  async renew() {
    /**
     * Try to load user from storage before login
     */
    this[symbols.user] = await this[symbols.userManager].getUser();

    if (!this[symbols.user] || !this[symbols.user].expired) {
      this::refreshDefaultMetaDataContext();

      return this.getAccessObject();
    }

    try {
      await this[symbols.userManager].signinSilent({
        data: {
          type: 'renew',
        },
      });
    } catch (e) {
      // Pass...
    }

    return this.getAccessObject();
  }

  async clear() {
    await this[symbols.userManager].removeUser();

    return null;
  }

  getIdentity() {
    const { profile } = this[symbols.user] || {};

    if (!profile) {
      return null;
    }

    return normalizeIdentity({
      id: profile.employee_id,
      roles: profile.roles,
    });
  }

  getAccessObject() {
    return toAccessObject(this[symbols.user] || {});
  }

  getRequestHeaders() {
    const { accessToken } = this.getAccessObject();

    return accessToken ? {
      [AUTHORIZATION]: `Bearer ${accessToken}`,
    } : {};
  }

  static async processCallback() {
    const manager = createManager(defaultSettings);
    const hash = get(global, 'location.hash', '').substr(1);
    const search = get(global, 'location.search', hash).substr(1);
    const query = qs.parse(hash || search);
    let stateData = {};

    if (query.state) {
      const storageString = await manager.settings.stateStore.get(query.state);
      const { data } = JSON.parse(storageString);
      stateData = data;
    }

    const {
      type,
      transition,
      redirectUrl,
    } = stateData;

    if (type === 'signIn') {
      if (transition === 'popup') {
        await manager.signinPopupCallback();
      }

      if (transition === 'redirect') {
        await manager.signinRedirectCallback();
        global.location.replace(redirectUrl);
      }

      return;
    }

    if (type === 'renew' || isIframe()) {
      manager.signinSilentCallback();

      return;
    }

    if (transition === 'popup' || isPopup()) {
      await manager.signoutPopupCallback();
    } else {
      await manager.signoutRedirectCallback();
      global.location.replace(redirectUrl || './');
    }
  }
}



// WEBPACK FOOTER //
// ./src/Auth/providers/OidcProvider.js